import React from 'react';
import { TFunction } from 'react-i18next';
import {
  Text,
  Link,
  CodeSnippet,
  Stack,
  Trans,
  CodeInline,
} from '@piwikpro/ui-components';
import { App } from '../../../model';


const PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES = {
  FLUTTER: '@platform/command/appInstallation/flutter',
};

const getFlutterSnippetCode = (trackingDomain, siteId) => `await FlutterPiwikPro.sharedInstance.configureTracker(baseURL: 'https://${trackingDomain}', siteId: '${siteId}');`;

export const FLUTTER_SNIPPET_INSTALLATION_CODE = `dart pub add flutter_piwikpro
# or
flutter pub add flutter_piwikpro`;

export const FLUTTER_SNIPPET_IMPORT_CODE = 'import \'package:flutter_piwikpro/flutter_piwikpro.dart\';';

interface FlutterInstallProps {
  app: App
  trackingDomain: string
  trackingDomainFailed: boolean
  t: TFunction
  handleCopyToClipboard: (result: boolean, productAnalyticsActionType: string) => void
}

const FlutterInstall: React.FC<FlutterInstallProps> = ({
  app,
  trackingDomain,
  trackingDomainFailed,
  t,
  handleCopyToClipboard,
}) => (
  <Stack vertical>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.flutter.new-instruction.line1.part1')}
      </Text>
    </Stack.Item>
    <Stack.Item fill>
      <Stack fullWidth>
        <Stack.Item />
        <Stack.Item fill>
          <CodeSnippet
            error={trackingDomainFailed}
            code={FLUTTER_SNIPPET_INSTALLATION_CODE}
            onCopy={result => handleCopyToClipboard(
              result,
              PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.FLUTTER,
            )}
            mode="no-highlight"
          />
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        <Trans
          i18nKey="apps.details.installation.flutter.new-instruction.line2"
          components={[
            <CodeInline dataId="TextStyle">flutter_piwikpro </CodeInline>,
          ]}
        />
      </Text>
    </Stack.Item>
    <Stack.Item fill>
      <Stack fullWidth>
        <Stack.Item />
        <Stack.Item fill>
          <CodeSnippet
            error={trackingDomainFailed}
            code={FLUTTER_SNIPPET_IMPORT_CODE}
            onCopy={result => handleCopyToClipboard(
              result,
              PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.FLUTTER,
            )}
            mode="no-highlight"
          />
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.flutter.new-instruction.line3')}
      </Text>
    </Stack.Item>
    <Stack.Item fill>
      <Stack fullWidth>
        <Stack.Item />
        <Stack.Item fill>
          <CodeSnippet
            error={trackingDomainFailed}
            code={getFlutterSnippetCode(trackingDomain, app.id)}
            onCopy={result => handleCopyToClipboard(
              result,
              PRODUCT_ANALYTICS_INSTAL_METHOD_ACTION_TYPES.FLUTTER,
            )}
            mode="no-highlight"
          />
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        <Trans
          i18nKey="apps.details.installation.flutter.new-instruction.line4"
          components={[
            <Link href="https://developers.piwik.pro/docs/flutter-sdk">Set up the SDK</Link>,
          ]}
        />
      </Text>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        <Trans
          i18nKey="apps.details.installation.flutter.new-instruction.line5"
          components={[
            <Link href={`/analytics/#/${app.id}/settings/debugger/`}>tracker debugger</Link>,
          ]}
        />
      </Text>
    </Stack.Item>
    <Stack.Item>
      <Text as="p">
        {t('apps.details.installation.instruction.help.part1')}
        <Link href="https://developers.piwik.pro/en/latest/data_collection/mobile/Piwik_PRO_SDK_for_Flutter.html">
          {t('apps.details.installation.links.installGuide')}
        </Link>
        {t('apps.details.installation.instruction.help.part2')}
        <Link href="http://community.piwik.pro/">
          {t('apps.details.installation.links.community')}
        </Link>
        .
      </Text>
    </Stack.Item>
  </Stack>
);

export default FlutterInstall;
